import { useTheme } from '@simplywallst/ui-core';

import PencilOutline from '@simplywallst/ui-core/icons/PencilOutline';
import PriceTargetOutline from '@simplywallst/ui-core/icons/PriceTargetOutline';
import UserOutline from '@simplywallst/ui-core/icons/UserOutline';
import WarrenSolid from '@simplywallst/ui-core/icons/WarrenSolid';

import type { CurrencyISO } from '@/constants/currencies';

import { FairValue } from './FairValue';

import { NarrativeAuthorAvatar } from '@/components/NarrativeAuthor';
import { useTracking } from 'react-tracking';
import { cn, Button } from '@simplywallst/ui-core/next';

export interface Props {
  currencyISO: CurrencyISO;
  narrativeFairValue: {
    isSelected: boolean;
    fairValue: number | null;
    type: string | null;
    valuationId: string | null;
    owner: {
      displayName: string | null;
      imageUrl: string | null;
    } | null;
  };
  npvPerShare: number | null;
  onClick: () => void;
}

const NarrativeFairValue = ({
  currencyISO,
  narrativeFairValue,
  npvPerShare,
  onClick,
}: Props) => {
  const theme = useTheme();
  const { trackEvent } = useTracking({
    type: 'track',
  });
  const { fairValue, isSelected, owner, type, valuationId } =
    narrativeFairValue;

  const avatarClasses = cn(!isSelected && 'opacity-80');

  return (
    <Button
      className="w-full bg-surface-3"
      variant="subtle"
      onClick={() => {
        trackEvent({
          subject: 'narrative-fair-value',
          action: 'click',
        });
        onClick();
      }}
      data-cy-id="narrative-fair-value"
    >
      <div className="grid w-full grid-cols-[min-content_1fr_min-content] items-center gap-x2">
        {(() => {
          if (valuationId === null) {
            return (
              <FairValue
                value={npvPerShare}
                icon={
                  <WarrenSolid
                    height={20}
                    width={20}
                    className={avatarClasses}
                  />
                }
                currencyISO={currencyISO}
                isSelected={isSelected}
              />
            );
          }
          switch (type) {
            case 'dcf':
              return (
                <FairValue
                  value={fairValue}
                  icon={
                    <WarrenSolid
                      height={20}
                      width={20}
                      className={avatarClasses}
                    />
                  }
                  currencyISO={currencyISO}
                  isSelected={isSelected}
                />
              );
            case 'legacy':
              return (
                <FairValue
                  value={fairValue}
                  icon={
                    <UserOutline
                      fill={theme.color.white}
                      height={20}
                      width={20}
                      className={avatarClasses}
                    />
                  }
                  currencyISO={currencyISO}
                  isSelected={isSelected}
                />
              );
            case 'user_defined':
              return (
                <FairValue
                  value={fairValue}
                  icon={
                    <div className={avatarClasses}>
                      {owner ? (
                        <NarrativeAuthorAvatar
                          size="sm"
                          owner={{
                            displayName:
                              owner?.displayName ?? 'Simply Wall St User',
                            imageUrl: owner?.imageUrl ?? null,
                          }}
                        />
                      ) : (
                        <UserOutline
                          fill={theme.color.white}
                          height={20}
                          width={20}
                        />
                      )}
                    </div>
                  }
                  currencyISO={currencyISO}
                  isSelected={isSelected}
                />
              );
            case 'analysts':
              return (
                <FairValue
                  value={fairValue}
                  icon={
                    <PriceTargetOutline
                      fill={theme.color.brand01}
                      className={avatarClasses}
                    />
                  }
                  currencyISO={currencyISO}
                  isSelected={isSelected}
                />
              );
            default:
              return (
                <FairValue
                  value={npvPerShare}
                  icon={
                    <WarrenSolid
                      height={20}
                      width={20}
                      className={avatarClasses}
                    />
                  }
                  currencyISO={currencyISO}
                  isSelected={isSelected}
                />
              );
          }
        })()}

        <PencilOutline fill={theme.color.brand01} className={avatarClasses} />
      </div>
    </Button>
  );
};

export default NarrativeFairValue;
